import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './globals.css';
import LandingPage from './welcomePage';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import FacultyDashboard from './faculty/FacultyDashboard';
import SubmitWork from './faculty/SubmitWork';
import MySubmissions from './faculty/MySubmissions';
import SubmissionDetails from './faculty/SubmissionDetails';
import FacultyProfile from './faculty/FacultyProfile';
import AdminDashboard from './admin/AdminDashboard';
import SubmissionsManagement from './admin/SubmissionsManagement';
import SubmissionReview from './admin/SubmissionReview';
import FacultyManagement from './admin/FacultyManagement';
import ArchivePage from './admin/ArchivePage';
import AboutPage from './AboutPage';
import { authAPI } from '../services/api';
import { Toaster } from '../ui/sonner';

// Authentication context
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'faculty';
  department?: string;
  position?: string;
  bio?: string;
  profile_picture?: string;
}

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  register: (
    name: string,
    email: string,
    password: string,
    department_id: number | null,
    position: string
  ) => Promise<{ success: boolean; message?: string }>;
}

export const AuthContext = React.createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  login: async () => ({ success: false }),
  logout: () => {},
  register: async () => ({ success: false }),
});

function App() {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('imdc_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const result = await authAPI.login(email, password);
      const authUser = result?.user || result?.data?.user;
      
      if (!result.error && authUser) {
        const loggedInUser: User = {
          id: authUser.id.toString(),
          name: authUser.name,
          email: authUser.email,
          role: authUser.role,
          department: authUser.department,
          position: authUser.position,
          bio: authUser.bio,
          profile_picture: authUser.profile_picture,
        };
        setUser(loggedInUser);
        localStorage.setItem('imdc_user', JSON.stringify(loggedInUser));
        return { success: true };
      }

      return { success: false, message: result?.message || 'Login failed.' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An unexpected error occurred.' };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    localStorage.removeItem('imdc_user');
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    department_id: number | null,
    position: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const result = await authAPI.register(name, email, password, department_id, position);
      const authUser = result?.user || result?.data?.user;
      if (!result.error && authUser) {
        const newUser: User = {
          id: authUser.id.toString(),
          name: authUser.name,
          email: authUser.email,
          role: authUser.role,
          department: authUser.department,
          position: authUser.position,
        };
        setUser(newUser);
        localStorage.setItem('imdc_user', JSON.stringify(newUser));
        return { success: true };
      }
      return { success: false, message: result?.message || 'Registration failed.' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'An unexpected error occurred.' };
    }
  };

  const ProtectedRoute = ({
    children,
    requireRole,
  }: {
    children: React.ReactNode;
    requireRole?: 'admin' | 'faculty';
  }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (requireRole && user.role !== requireRole) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, register }}>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Faculty Routes */}
          <Route
            path="/faculty/dashboard"
            element={
              <ProtectedRoute requireRole="faculty">
                <FacultyDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faculty/submit"
            element={
              <ProtectedRoute requireRole="faculty">
                <SubmitWork />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faculty/submissions"
            element={
              <ProtectedRoute requireRole="faculty">
                <MySubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faculty/submissions/:id"
            element={
              <ProtectedRoute requireRole="faculty">
                <SubmissionDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faculty/profile"
            element={
              <ProtectedRoute requireRole="faculty">
                <FacultyProfile />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requireRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/submissions"
            element={
              <ProtectedRoute requireRole="admin">
                <SubmissionsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/submissions/:id"
            element={
              <ProtectedRoute requireRole="admin">
                <SubmissionReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faculty"
            element={
              <ProtectedRoute requireRole="admin">
                <FacultyManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/archive"
            element={
              <ProtectedRoute requireRole="admin">
                <ArchivePage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster richColors position="top-right" />
      </Router>
    </AuthContext.Provider>
  );
}

export default App;